import {
  Children,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Link } from '@tanstack/react-router';
import { ChevronDownIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ordinal,
  queuedReasonSentence,
  useWorkspaceConcurrencyLimit,
} from '@/features/concurrency';
import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';
import { NO_FAILURE_DETAIL } from '../executionStatus';
import { groupExecutions, type Attempt } from '../attempts';
import {
  useWorkspaceFileExistence,
  useWorkspaceFilePreviewLink,
} from '@/features/workspace-files';
import type { MarkdownFilePaths } from '@/shared/components/Markdown';
import { findWorkspacePaths } from '@/shared/components/markdownFilePaths';
import { MarkdownLazy } from '@/shared/components/MarkdownLazy';
import { PersonName } from '@/shared/components/PersonName';
import { DRAWER_PROSE_CLASS } from '../prose';
import {
  formatCost,
  formatDurationMs,
  formatTurns,
  runOutcome,
} from '../executionHistory';
import { useLiveDuration } from '../hooks/useLiveDuration';
import { stepDisplayName } from '../stepNames';
import type { RunOutcome } from '../executionHistory';
import { useSheetBodyScroller } from '../hooks/useSheetBodyScroller';
import { ExecutionDetail } from './ExecutionDetail';
import { RunHeader } from './RunHeader';
import { RunCredential } from './RunCredential';
import { runCredentialMark } from '../runCredential';
import { isTerminalStatus } from '../executionStatus';
import { useStopTask } from '../hooks/useTaskLifecycle';
import { isAskableRun } from '../runThread/replyState';
import { RunConversation } from '../runThread/RunConversation';
import { RunPrompt } from '../runThread/RunPrompt';
import { parseRunThreadHash } from '../runThread/threadLink';
import type {
  TaskExecutionSummary,
  TaskView,
  UserSummary,
} from '@/lib/api/generated/model';
import { useCanWork } from '@/features/workspaces/canWork';
import {
  AI_HELP_COPY,
  AiToolLine,
  useAiToolHelp,
  whyRunFailedAsk,
} from '@/features/editor-access';

export interface TaskExecutionsPanelProps {
  task: TaskView;
  /** Needed to fetch a run's transcript, which no longer rides the board's read (081). */
  workspaceId: string;
  /** The board's step labels by id, so a run is named in words (`stepDisplayName`). */
  stepLabels?: ReadonlyMap<string, string>;
  /**
   * This task's own workflow key, for the "Open run" link on a
   * `linked_workflow` run — the run page is addressed by the parent's
   * workflow and id. Without it the link is not offered.
   */
  workflowKey?: string;
  /**
   * The file already previewed beside this task: a further open is a replace
   * (spec 140 FR-040). From the drawer, as `TaskCommentsPanel` and
   * `TaskResultsPanel` take it — the address is the drawer's to read.
   */
  previewPath?: string;
  /**
   * Opens the task's Comments, where a message added to the task's context
   * went (spec 149 FR-005). The drawer supplies it; without it the message
   * still says it was added.
   */
  onOpenComments?: () => void;
}

/**
 * A first guess at a run's height, used only until the real one is measured.
 * A run with no summary and no failure detail is the common case and the
 * shortest, so the estimate is close to it: guessing low costs an extra
 * measure pass, guessing high leaves a gap at the end of the rail.
 */
const ESTIMATED_RUN_HEIGHT = 64;
/** An attempt header or a pass-through line. */
const ESTIMATED_LINE_HEIGHT = 32;

/**
 * What this task has actually done: every recorded run, newest first, with what
 * it produced or reported and — where the workflow offered a choice — which way
 * it went and why.
 *
 * Steps are named by the identifier the run recorded, not by resolving it
 * against the workflow as it stands now. A run carries a snapshot precisely
 * because the workflow can be edited afterwards; naming it from today's
 * definition would attribute to a past run a name it never had. The same rule
 * holds for the prompt an expanded run shows (spec 149): the one the run was
 * rendered with, never the workflow's current wording.
 *
 * An expanded run that had an agent shows, in order, its Prompt (collapsed),
 * its transcript and its Conversation (spec 149 Q5).
 *
 * Drawn as a rail with a node per run rather than as stacked boxes, because
 * these are one sequence and stacked boxes say only "several things happened".
 *
 * Virtualised, per constitution Principle 6, which names the execution list
 * among the lists that qualify by default: a task that retries a step records
 * a run each time, so the count is bounded by the work rather than by anything
 * the UI controls. Runs are measured rather than assumed to be one height — a
 * run carrying a failure message is several times the height of one that
 * completed silently.
 */
export function TaskExecutionsPanel({
  task,
  workspaceId,
  stepLabels,
  workflowKey,
  previewPath,
  onOpenComments,
}: TaskExecutionsPanelProps) {
  const attempts = groupExecutions(task.executions ?? []);
  /*
    The workspace paths the recorded summaries name, as links to the preview
    panel beside the sheet (spec 140 FR-058). Checked once for the whole rail
    rather than per run — one listing per distinct folder, shared with the file
    browser's cache — because a task that retried a step names the same files
    in every attempt.
  */
  const previewLink = useWorkspaceFilePreviewLink(workspaceId, previewPath);
  const summaries = (task.executions ?? [])
    .map((execution) => execution.result_summary)
    .filter((summary): summary is string => Boolean(summary));
  const summaryText = summaries.join('\n');
  const mentioned = useMemo(
    () => [...new Set(findWorkspacePaths(summaryText))],
    [summaryText],
  );
  const existence = useWorkspaceFileExistence(workspaceId, mentioned);
  const filePaths: MarkdownFilePaths = {
    exists: existence.exists,
    linkTo: previewLink,
  };
  /*
    A link into a run's Conversation (spec 149 FR-006, a `run_replied`
    notification): read once, as the tab opens. That run starts expanded and
    its attempt open, the rail scrolls to it, and its Conversation scrolls to
    the message. Read from the address rather than handed down, so the tab
    needs no router to render; a hash naming a run this task does not have is
    ignored.
  */
  const [focus] = useState(() => {
    const parsed = parseRunThreadHash(window.location.hash);
    return parsed &&
      (task.executions ?? []).some((run) => run.id === parsed.executionId)
      ? parsed
      : null;
  });
  /*
    Which attempts are open. The newest and any with a run in flight are open
    unless the reader closed them; every other one is closed unless they opened
    it (spec 127 FR-003). Stored as the reader's overrides, so an attempt that
    appears while the drawer is open — the task started again — opens by
    itself, and the one it displaced closes.
  */
  const [overrides, setOverrides] = useState<ReadonlyMap<number, boolean>>(
    () => {
      const holding = focus
        ? attempts.find((attempt) =>
            attempt.items.some(
              (entry) =>
                entry.kind === 'run' &&
                entry.execution.id === focus.executionId,
            ),
          )
        : undefined;
      return new Map(holding ? [[holding.ordinal, true]] : []);
    },
  );
  const newest = attempts[0]?.ordinal;
  const isOpen = (attempt: Attempt) =>
    overrides.get(attempt.ordinal) ??
    (attempt.ordinal === newest || attempt.isLive);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(focus ? [focus.executionId] : []),
  );
  const items = flatten(attempts, isOpen);
  const listRef = useRef<HTMLOListElement>(null);
  const { getScrollElement, scrollMargin } = useSheetBodyScroller(listRef);
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual's returned functions can't be safely memoized by the React Compiler; this is expected.
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement,
    estimateSize: (index) =>
      items[index]?.kind === 'run'
        ? ESTIMATED_RUN_HEIGHT
        : ESTIMATED_LINE_HEIGHT,
    getItemKey: (index) => items[index]?.key ?? index,
    scrollMargin,
    overscan: 6,
  });

  // The landing's scroll, once: the focused run's row may be out of the
  // virtualiser's window, and its Conversation cannot scroll to a message
  // that is not drawn.
  const landed = useRef(false);
  const focusIndex = focus
    ? items.findIndex(
        (item) => item.kind === 'run' && item.key === focus.executionId,
      )
    : -1;
  useEffect(() => {
    if (landed.current || focusIndex < 0) return;
    landed.current = true;
    virtualizer.scrollToIndex(focusIndex, { align: 'start' });
  }, [focusIndex, virtualizer]);

  if (attempts.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        This task has not run yet.
      </p>
    );
  }

  // A `Set` copy per toggle rather than mutating in place — `expandedIds` is
  // read by React as state, and mutating it wouldn't trigger a re-render.
  function toggleExpanded(id: string, open: boolean) {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }
  function toggleAttempt(attempt: Attempt) {
    setOverrides((current) =>
      new Map(current).set(attempt.ordinal, !isOpen(attempt)),
    );
  }

  return (
    // `relative` so each item positions against this element, and a height
    // rather than content because the items are taken out of flow — the
    // virtualiser's total is what gives the list its scrollable size.
    <ol
      ref={listRef}
      className="relative"
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map((virtualItem) => {
        const item = items[virtualItem.index];
        if (!item) return null;
        // `top`, not a transform: see the note on runs below.
        const style = { top: `${String(virtualItem.start - scrollMargin)}px` };

        if (item.kind === 'attempt') {
          return (
            <li
              key={item.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              className="absolute right-0 left-0 pb-3"
              style={style}
            >
              <AttemptHeader
                attempt={item.attempt}
                open={isOpen(item.attempt)}
                onToggle={() => {
                  toggleAttempt(item.attempt);
                }}
              />
            </li>
          );
        }

        if (item.kind === 'pass-through') {
          return (
            <li
              key={item.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              className={`absolute right-0 left-0 grid grid-cols-[0.75rem_1fr] gap-x-3 ${
                item.last ? 'pb-4' : 'pb-5'
              }`}
              style={style}
            >
              <RunMarker
                colourClass="text-muted-foreground/50"
                isOldest={item.last}
              />
              <PassThroughLine
                executions={item.executions}
                steps={item.steps.map((step) =>
                  stepDisplayName(step, stepLabels),
                )}
              />
            </li>
          );
        }

        const execution = item.execution;
        // Read once and handed to both halves: the node's colour and the
        // outcome the run names in words are the same fact, and deriving it
        // twice is how they would come to disagree.
        const outcome = runOutcome(execution.status);
        return (
          <li
            key={item.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            // A fixed gutter column for the rail and a flexible one for the
            // run, so the node and its connector are centred on one axis by
            // construction rather than by two offsets that have to be kept
            // agreeing with each other.
            //
            // Bottom padding rather than a gap on the list: absolutely
            // positioned children do not participate in flex or grid spacing,
            // and padding is inside the measured height, so the spacing
            // survives measurement.
            className={`absolute right-0 left-0 grid grid-cols-[0.75rem_1fr] gap-x-3 ${
              item.last ? 'pb-4' : 'pb-5'
            }`}
            /*
              `top`, not the `transform: translateY()` the virtualizer's docs
              suggest. Sticky positioning is computed from layout, and a
              transform is applied afterwards to whatever layout produced — so
              the run header (`RunHeader`, sticky) inside a translated row was
              pinned as if the row sat at the top of the list and then carried
              down by the row's own translation: the third run's header stuck a
              third of the way down the drawer. Measured in Chromium: a header
              in a row translated by 420px pinned 217px below the scroller's
              top; the same header in a row placed with `top` pinned at the
              top. Positioning with `top` costs a layout per scroll frame on a
              list of tens of rows, which is nothing.
            */
            style={style}
          >
            <RunMarker colourClass={outcome.colourClass} isOldest={item.last} />
            <div className="flex min-w-0 flex-col gap-1.5">
              <Collapsible
                open={expandedIds.has(execution.id)}
                onOpenChange={(open) => toggleExpanded(execution.id, open)}
              >
                <ExecutionRun
                  task={task}
                  execution={execution}
                  outcome={outcome}
                  name={runName(execution, stepLabels)}
                  filePaths={filePaths}
                />
                {/*
                  The way back to a fan-out once the parent has moved on: each
                  `linked_workflow` run opens its own batch on the run page,
                  open or finished — so a step visited twice offers both.
                */}
                {workflowKey &&
                execution.step_kind_snapshot === 'linked_workflow' ? (
                  <Link
                    to="/workspaces/$workspaceId/workflows/$workflowKey/tasks/$taskId/run"
                    params={{ workspaceId, workflowKey, taskId: task.id }}
                    search={{ execution: execution.id }}
                    className="text-muted-foreground hover:text-foreground w-fit text-xs underline underline-offset-4"
                  >
                    Open run
                  </Link>
                ) : null}
                {execution.status === 'queued' ? (
                  <QueuedRunLine
                    execution={execution}
                    workspaceId={workspaceId}
                    taskId={task.id}
                  />
                ) : null}
                {/* Before the panel, not inside it: the panel clips for its
                    animation, and a sticky header inside a clipping box sticks
                    to that box. See `RunHeader`. */}
                {expandedIds.has(execution.id) ? (
                  <RunHeader
                    step={runName(execution, stepLabels)}
                    outcome={outcome}
                    live={!isTerminalStatus(execution.status)}
                  />
                ) : null}
                <CollapsiblePanel className="flex flex-col gap-3 pt-2">
                  {isAskableRun(execution) ? (
                    <RunPrompt
                      workspaceId={workspaceId}
                      taskId={task.id}
                      executionId={execution.id}
                    />
                  ) : null}
                  <ExecutionDetail
                    workspaceId={workspaceId}
                    taskId={task.id}
                    executionId={execution.id}
                    isOpen={expandedIds.has(execution.id)}
                    isFinished={isTerminalStatus(execution.status)}
                    maxTurns={execution.max_turns}
                  />
                  {/* Mounted only while expanded: it reads the thread. */}
                  {isAskableRun(execution) && expandedIds.has(execution.id) ? (
                    <RunConversation
                      workspaceId={workspaceId}
                      taskId={task.id}
                      execution={execution}
                      onOpenComments={onOpenComments}
                      focusMessageId={
                        focus?.executionId === execution.id
                          ? focus.messageId
                          : null
                      }
                    />
                  ) : null}
                </CollapsiblePanel>
              </Collapsible>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** What the virtualised list draws, in order: attempt headers and, when open, their items. */
type PanelItem =
  | { kind: 'attempt'; key: string; attempt: Attempt }
  | { kind: 'run'; key: string; execution: TaskExecutionSummary; last: boolean }
  | {
      kind: 'pass-through';
      key: string;
      executions: TaskExecutionSummary[];
      steps: string[];
      last: boolean;
    };

function flatten(
  attempts: readonly Attempt[],
  isOpen: (attempt: Attempt) => boolean,
): PanelItem[] {
  const items: PanelItem[] = [];
  // One attempt needs no heading: it would repeat its only run's outcome and
  // cost directly above them.
  const headed = attempts.length > 1;
  for (const attempt of attempts) {
    if (headed) {
      items.push({
        kind: 'attempt',
        key: `attempt-${String(attempt.ordinal)}`,
        attempt,
      });
      if (!isOpen(attempt)) continue;
    }
    attempt.items.forEach((entry, index) => {
      const last = index === attempt.items.length - 1;
      if (entry.kind === 'run') {
        items.push({
          kind: 'run',
          key: entry.execution.id,
          execution: entry.execution,
          last,
        });
      } else {
        items.push({
          kind: 'pass-through',
          key: `pass-${entry.executions[0]?.id ?? String(index)}`,
          executions: entry.executions,
          steps: entry.steps,
          last,
        });
      }
    });
  }
  return items;
}

const ATTEMPT_OUTCOME: Record<Attempt['outcome'], string> = {
  completed: 'Completed',
  failed: 'Failed',
  stopped: 'Stopped',
  blocked: 'Blocked',
  queued: 'Queued',
  live: 'Running',
  passed: 'Passed through',
};

/**
 * A line of facts with a dot between each (spec 178): "Bob Demo · shared
 * account · 2h ago · 3m 6s · claude-sonnet-5 · $0.6861". Run together with
 * only space between them, the facts read as one phrase. A fact that is absent
 * leaves no dot behind, and the dots are not read aloud.
 */
function Facts({ children }: { children: ReactNode }) {
  return Children.toArray(children).flatMap((fact, index) =>
    index === 0
      ? [fact]
      : [
          <span key={`dot-${String(index)}`} aria-hidden="true">
            ·
          </span>,
          fact,
        ],
  );
}

/**
 * The person a run or an attempt is attributed to (spec 158). The words are for
 * assistive technology: on screen the line's facts carry no labels, but read
 * aloud a bare name between a status and a time says nothing.
 */
function StartedBy({ person }: { person: UserSummary }) {
  return (
    <span className="inline-flex min-w-0 items-baseline">
      <span className="sr-only">Started by </span>
      <PersonName person={person} />
    </span>
  );
}

/**
 * One trip through the workflow, as a line the reader can open (spec 127
 * FR-002, FR-003): its number, how it ended, the time spent running and what
 * it cost. A closed attempt that holds a failure says so, so the reader knows
 * which closed one to open.
 */
function AttemptHeader({
  attempt,
  open,
  onToggle,
}: {
  attempt: Attempt;
  open: boolean;
  onToggle: () => void;
}) {
  const cost =
    attempt.cost.kind === 'exact' || attempt.cost.kind === 'atLeast'
      ? `${attempt.cost.kind === 'atLeast' ? 'at least ' : ''}${formatCost(
          attempt.cost.amount,
          attempt.cost.currency,
        )}`
      : null;
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="hover:bg-accent/50 focus-visible:ring-ring flex w-full items-baseline gap-2 rounded-md px-1 py-1 text-left focus-visible:ring-3 focus-visible:outline-none"
    >
      <ChevronDownIcon
        aria-hidden="true"
        className={`text-muted-foreground size-3.5 shrink-0 self-center transition-transform ${
          open ? '' : '-rotate-90'
        }`}
      />
      {/* One line, whatever the facts beside it need: on a phone a long fact
          line squeezed it into "Attempt" over "14" (spec 184). */}
      <span className="text-foreground shrink-0 text-sm font-medium whitespace-nowrap">
        Attempt {attempt.ordinal}
      </span>
      <span className="text-muted-foreground flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-xs">
        <Facts>
          <span>{ATTEMPT_OUTCOME[attempt.outcome]}</span>
          {attempt.startedBy ? <StartedBy person={attempt.startedBy} /> : null}
          {attempt.startedAt ? (
            <time title={formatExactTime(attempt.startedAt)}>
              {formatRelativeTime(attempt.startedAt)}
            </time>
          ) : null}
          {attempt.workMs > 0 ? (
            <span>{formatDurationMs(attempt.workMs)} running</span>
          ) : null}
          {cost ? <span className="tabular-nums">{cost}</span> : null}
          {!open && attempt.hasFailure ? (
            <span className="text-state-danger">includes a failed run</span>
          ) : null}
        </Facts>
      </span>
    </button>
  );
}

/**
 * Steps the task went through without anything running — Backlog, a gate
 * someone approved, Done — as one quiet line rather than a row each (spec 127
 * FR-001). Opening it lists them.
 */
function PassThroughLine({
  executions,
  steps,
}: {
  executions: TaskExecutionSummary[];
  steps: string[];
}) {
  return (
    <Collapsible>
      <CollapsibleTrigger className="text-muted-foreground hover:text-foreground group flex min-w-0 items-baseline gap-1.5 text-left text-xs">
        <ChevronDownIcon
          aria-hidden="true"
          className="size-3 shrink-0 -rotate-90 self-center transition-transform group-data-open:rotate-0"
        />
        <span>Passed {steps.join(', ')}</span>
      </CollapsibleTrigger>
      <CollapsiblePanel className="pt-1">
        <ul className="text-muted-foreground flex flex-col gap-0.5 ps-5 text-xs">
          {executions.map((execution) => (
            <li key={execution.id} className="flex gap-2">
              <span className="font-mono">{execution.step_id_snapshot}</span>
              <time title={formatExactTime(execution.created_at)}>
                {formatRelativeTime(execution.created_at)}
              </time>
            </li>
          ))}
        </ul>
      </CollapsiblePanel>
    </Collapsible>
  );
}

/**
 * The node on the rail, and the line from it to the run below.
 *
 * The connector belongs to the run above it rather than to the list, which is
 * what keeps the rail honest: drawn as a border on the list it spans the
 * container, so it starts short of the newest node and hangs past the oldest
 * one by whatever that run happened to be tall. Drawn here it runs from this
 * node's bottom edge to the next node's top edge and can do nothing else,
 * however tall either run is and however the virtualiser measures them.
 *
 * The node takes the run's outcome colour. A rail of identical dots says only
 * "several things happened"; a red one in a column of green is the thing
 * someone scanning a failed task is looking for, and it costs a reader nothing
 * to ignore. The colour comes from the same table every other surface reads,
 * through `bg-current`, so there is no second opinion about what red means.
 */
function RunMarker({
  colourClass,
  isOldest,
}: {
  colourClass: string;
  isOldest: boolean;
}) {
  return (
    // Decorative: the run beside it already names its own outcome in words.
    <span aria-hidden="true" className={`relative ${colourClass}`}>
      <span className="bg-current absolute top-1.5 left-1/2 size-2 -translate-x-1/2 rounded-full" />
      {isOldest ? null : (
        // From this node's bottom edge (`top-1.5` + `size-2`) to the next
        // node's top edge. The extra 0.75rem is the run's bottom padding
        // (1.25rem) less the node's own offset (0.5rem) — percentage height
        // resolves against this gutter, which stretches to the run's height.
        <span className="bg-border absolute top-[0.875rem] left-1/2 h-[calc(100%+0.75rem)] w-px -translate-x-1/2" />
      )}
    </span>
  );
}

/**
 * Where a queued run stands and why, with its own Stop (spec 115 FR-003).
 *
 * Stop sits here as well as in the task's menu: this is the sentence that explains the
 * wait, and deciding not to wait belongs beside it. The service ends a queued run at
 * once — it is a request, not work — so nothing here waits on a confirmation.
 */
function QueuedRunLine({
  execution,
  workspaceId,
  taskId,
}: {
  execution: TaskExecutionSummary;
  workspaceId: string;
  taskId: string;
}) {
  const limit = useWorkspaceConcurrencyLimit();
  const stop = useStopTask();
  const canWork = useCanWork();
  const place =
    execution.queue_position == null
      ? 'Queued'
      : `Queued — ${ordinal(execution.queue_position)} in line`;
  const reason = queuedReasonSentence(execution.queued_reason, limit);
  // The reason continues the sentence, so it starts lower-case after the dot.
  const sentence = reason
    ? `${place} · ${reason.charAt(0).toLowerCase()}${reason.slice(1)}`
    : place;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
      <p className="text-muted-foreground text-xs">{sentence}</p>
      {canWork ? (
        <Button
          size="sm"
          variant="outline"
          disabled={stop.isPending}
          aria-label="Stop this queued run"
          onClick={() =>
            stop.mutate(
              { workspaceId, taskId },
              { onError: (error) => toast.error(error.message) },
            )
          }
        >
          {stop.isPending ? 'Stopping…' : 'Stop'}
        </Button>
      ) : null}
    </div>
  );
}

/** One recorded run. Split out so the list above is only about windowing. */
function ExecutionRun({
  task,
  execution,
  outcome,
  name,
  filePaths,
}: {
  /** Named in the ask a failed run offers for an AI tool (spec 248). */
  task: { id: string; title: string };
  execution: TaskExecutionSummary;
  outcome: RunOutcome;
  /** The step in words, not its id. */
  name: string;
  /** How a path this run's summary mentions reaches the file (spec 140 FR-058). */
  filePaths?: MarkdownFilePaths;
}) {
  // Counts up while the run is live, frozen once it ends (spec 097 FR-011).
  const duration = useLiveDuration(
    execution,
    !isTerminalStatus(execution.status),
  );
  const failed = execution.status === 'failed';
  const aiHelp = useAiToolHelp();

  return (
    <>
      <div className="flex items-baseline justify-between gap-3">
        <CollapsibleTrigger className="group flex min-w-0 items-baseline gap-1.5 text-left">
          <ChevronDownIcon
            aria-hidden="true"
            className="text-muted-foreground size-3.5 shrink-0 -rotate-90 self-center transition-transform group-data-open:rotate-0"
          />
          <span className="text-foreground text-sm font-medium">{name}</span>
        </CollapsibleTrigger>
        <span className={`${outcome.colourClass} text-xs font-medium`}>
          {outcome.label}
        </span>
      </div>

      {/*
        The recorded status stays, beside the outcome rather than instead
        of it: the outcome is what a reader wants, and the raw value is
        what someone diagnosing a stuck run needs — `orchestrating` and
        `provisioning` both read as "In Progress" and mean different
        things to whoever is looking for where it stopped.
      */}
      <div className="text-muted-foreground flex flex-wrap items-baseline gap-x-1.5 text-xs">
        <Facts>
          {/* Printed only when it adds to the outcome word at the right
            (spec 127 FR-004): "provisioning" says more than "In progress";
            "completed" beside "Completed" says nothing. */}
          {execution.status !== outcome.label.toLowerCase() ? (
            <span className="font-mono">{execution.status}</span>
          ) : null}
          {/* Whom the run is for (spec 158): the person its commits name as
            author. Nobody is shown as nothing, as model and cost are. */}
          {execution.started_by ? (
            <StartedBy person={execution.started_by} />
          ) : null}
          {/* Which account it acted as on its forges (spec 159): the person's
            linked one, or the shared one and why. */}
          {/* Only when it has something to say: an empty one would leave two
            dots side by side. */}
          {runCredentialMark(execution) ? (
            <RunCredential execution={execution} />
          ) : null}
          <time title={formatExactTime(execution.created_at)}>
            {formatRelativeTime(execution.created_at)}
          </time>
          {duration ? <span>{duration}</span> : null}
          {/*
          What ran it and what it cost, when the run recorded them (spec 096
          FR-017). Both were on the payload and shown nowhere, and both are the
          next thing a person scanning failed runs asks. No placeholder when
          absent: an older run simply has less to say.
        */}
          {execution.model_snapshot ? (
            <span className="break-all">{execution.model_snapshot}</span>
          ) : null}
          {execution.cost != null ? (
            <span className="tabular-nums">
              {formatCost(execution.cost, execution.currency)}
            </span>
          ) : null}
          {/*
          How close the run came to its configured limit (spec 102) — terminal-
          state data only, and omitted entirely (not a placeholder) when either
          figure is missing, matching model/cost's own convention above.
        */}
          {isTerminalStatus(execution.status) &&
          formatTurns(execution.num_turns, execution.max_turns) ? (
            <span className="tabular-nums">
              {formatTurns(execution.num_turns, execution.max_turns)}
            </span>
          ) : null}
        </Facts>
      </div>

      {execution.result_summary ? (
        // The run's own account of itself, written as markdown by the agent's
        // condensation step — rendered as such, so a bullet is a bullet.
        <MarkdownLazy
          content={execution.result_summary}
          className={DRAWER_PROSE_CLASS}
          filePaths={filePaths}
        />
      ) : null}

      {failed ? (
        // As written, never as markdown: a stack trace full of `*` and `#` means
        // exactly what it says, and the one thing a reader needs is to read it
        // exactly (FR-005).
        <p className="text-foreground text-sm leading-relaxed break-words whitespace-pre-wrap">
          {execution.error_message?.trim() || NO_FAILURE_DETAIL}
        </p>
      ) : null}

      {/* Where the person is already reading why it failed (spec 248). */}
      {failed && aiHelp !== 'hidden' ? (
        <AiToolLine
          help={aiHelp}
          ask={whyRunFailedAsk(task, execution.id)}
          copyWords={{ label: AI_HELP_COPY.runCopy }}
          setupWords={{ label: AI_HELP_COPY.runSetUp }}
        />
      ) : null}

      {execution.chosen_transition_key ? (
        <p className="text-muted-foreground text-xs">
          Chose{' '}
          <span className="text-foreground font-mono">
            {execution.chosen_transition_key}
          </span>
          {execution.chosen_transition_reason ? (
            <>
              {' — '}
              <span className="text-foreground break-words">
                {execution.chosen_transition_reason}
              </span>
            </>
          ) : null}
        </p>
      ) : null}
    </>
  );
}

/**
 * What a run is called in the list: its step's name, and for the run that chose a
 * sub-workflow step's way out once its tasks were done, what it did there — it
 * shares the step with the run that created them.
 */
function runName(
  execution: TaskExecutionSummary,
  labels?: ReadonlyMap<string, string>,
): string {
  const step = stepDisplayName(execution.step_id_snapshot, labels);
  return execution.is_join_decision ? `${step} · Choosing next step` : step;
}
